import { type Capability } from '@sisoma/auth';
import { isNavGroup, type NavLink, type NavSection } from '@sisoma/ui';

/**
 * Sidebar navigation registry — the single source of truth for what appears
 * in the sidenav.
 *
 * To add a link:
 *   1. Register a route (usually lazy) in `app.routes.ts`.
 *   2. Add an entry below with the same path.
 *   3. Need a new icon? Register it in the `@sisoma/ui` Icon component first
 *      (projects/ui/src/lib/icon/icon.ts).
 *
 * To add a collapsible group, give an entry `children` instead of a `route`.
 * The parent then expands in place when clicked rather than navigating, and
 * opens automatically when one of its children is the active route.
 *
 * ## Capability gating (fp1-04/fp1-06)
 *
 * An entry may carry an optional `capability` — the sidenav only shows it when
 * the active partner grants that capability. Gating lives here, not in
 * `@sisoma/ui`, so the library's `NavLink`/`NavSection` types stay purely
 * presentational and auth-free. Run the registry through {@link filterNav}
 * before handing it to the sidenav (the `Shell` does this reactively).
 *
 * The admin surface is gated to `superAdmin` (who holds the full capability
 * catalogue), so filtering is a no-op here today — the seam mirrors the partner
 * app for consistency. No admin entry carries a `capability`.
 */

/** A `NavLink` that may be hidden unless the active partner grants `capability`. */
export interface GatedNavLink extends NavLink {
  capability?: Capability;
}

/** A collapsible group that may itself be gated, with gated child links. */
export interface GatedNavGroup {
  id?: string;
  label: string;
  icon: NavLink['icon'];
  capability?: Capability;
  children: GatedNavLink[];
}

export type GatedNavItem = GatedNavLink | GatedNavGroup;

/** A titled section that may itself be gated, holding gated entries. */
export interface GatedNavSection {
  heading?: string;
  capability?: Capability;
  items: GatedNavItem[];
}

export const NAV_SECTIONS: GatedNavSection[] = [
  {
    items: [
      { label: 'Dashboard', icon: 'dashboard', route: 'dashboard' },
      { label: 'Partner areas', icon: 'briefcase', route: 'partner-areas' },
      { label: 'SSO configuration', icon: 'key', route: 'sso-config' },
    ],
  },
];

/** Links pinned to the bottom of the sidenav. Groups are not supported here. */
export const FOOTER_NAV_ITEMS: GatedNavLink[] = [
  { label: 'Settings', icon: 'settings', route: 'settings' },
];

/**
 * Drop every entry the active partner cannot see, and return the plain
 * presentational `NavSection[]` the sidenav consumes.
 *
 * Pure — `has` is the only source of reactivity. An entry with no `capability`
 * is always kept; a group is kept only if it passes its own gate and still has
 * at least one visible child; a section that empties out is dropped.
 */
export function filterNav(
  sections: readonly GatedNavSection[],
  has: (capability: Capability) => boolean,
): NavSection[] {
  const allowed = (item: { capability?: Capability }): boolean =>
    item.capability === undefined || has(item.capability);

  return sections
    .filter(allowed)
    .map((section) => ({
      heading: section.heading,
      items: section.items.filter(allowed).flatMap((item): GatedNavItem[] => {
        if (!isGatedGroup(item)) {
          return [item];
        }
        const children = item.children.filter(allowed);
        return children.length > 0 ? [{ ...item, children }] : [];
      }),
    }))
    .filter((section) => section.items.length > 0);
}

/** Filter the footer links the same way (flat — no groups). */
export function filterNavLinks(
  links: readonly GatedNavLink[],
  has: (capability: Capability) => boolean,
): NavLink[] {
  return links.filter((link) => link.capability === undefined || has(link.capability));
}

/** True when a gated entry expands rather than navigates. Mirrors ui `isNavGroup`. */
function isGatedGroup(item: GatedNavItem): item is GatedNavGroup {
  return isNavGroup(item);
}
